const url = import.meta.env.VITE_SUPABASE_URL?.trim()?.replace(/\/$/,'');
const publishableKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY?.trim();

export const supabaseConfigured = Boolean(url && publishableKey);
export const CMS_ROW_ID = 'main';
export const MEDIA_BUCKET = 'luvera-media';
const SESSION_KEY='luvera_v7_supabase_session';
const AUTH_EVENT='luvera-auth-change';

type Session={access_token:string;refresh_token?:string;expires_at?:number;user?:unknown};
const readSession=():Session|null=>{try{return JSON.parse(localStorage.getItem(SESSION_KEY)||'null')}catch{return null}};
const writeSession=(s:Session|null)=>{if(s)localStorage.setItem(SESSION_KEY,JSON.stringify(s));else localStorage.removeItem(SESSION_KEY);window.dispatchEvent(new Event(AUTH_EVENT))};

async function refreshSession(session:Session):Promise<Session|null>{
 if(!url||!publishableKey||!session.refresh_token)return session;
 const res=await fetch(`${url}/auth/v1/token?grant_type=refresh_token`,{
   method:'POST',
   headers:{apikey:publishableKey,'Content-Type':'application/json'},
   body:JSON.stringify({refresh_token:session.refresh_token})
 });
 if(!res.ok){writeSession(null);return null}
 const data=await res.json();
 const next={...data,expires_at:Math.floor(Date.now()/1000)+(data.expires_in||3600)};
 writeSession(next);
 return next;
}

export async function getSession(){
 let s=readSession();
 if(s?.expires_at&&s.expires_at<Math.floor(Date.now()/1000)+60)s=await refreshSession(s);
 return s
}

export async function signInWithPassword(email:string,password:string){
 if(!url||!publishableKey)return {error:new Error('Supabase is not configured')};
 const res=await fetch(`${url}/auth/v1/token?grant_type=password`,{
   method:'POST',
   headers:{apikey:publishableKey,'Content-Type':'application/json'},
   body:JSON.stringify({email,password})
 });
 if(!res.ok)return {error:new Error('Invalid login')};
 const data=await res.json();
 writeSession({...data,expires_at:Math.floor(Date.now()/1000)+(data.expires_in||3600)});
 return {error:null};
}

export async function signOut(){
 const s=readSession();
 if(url&&publishableKey&&s?.access_token){
   try{
     await fetch(`${url}/auth/v1/logout`,{
       method:'POST',
       headers:{apikey:publishableKey,Authorization:`Bearer ${s.access_token}`}
     })
   }catch{}
 }
 writeSession(null)
}

export function onAuthStateChange(cb:()=>void){
 const fn=()=>cb();
 window.addEventListener(AUTH_EVENT,fn);
 return()=>window.removeEventListener(AUTH_EVENT,fn)
}

async function headers(extra:Record<string,string>={}){
 const s=await getSession();
 return {
   apikey:publishableKey||'',
   ...(s?.access_token ? {Authorization:`Bearer ${s.access_token}`} : {}),
   ...extra
 };
}

export async function isAdminSession():Promise<boolean>{
 if(!url||!publishableKey)return true;
 const session=await getSession();
 if(!session?.access_token)return false;
 try{
   const res=await fetch(`${url}/rest/v1/rpc/is_luvera_admin`,{
     method:'POST',
     headers:await headers({'Content-Type':'application/json'}),
     body:'{}'
   });
   if(!res.ok)return false;
   const value=await res.json();
   return value===true;
 }catch{
   return false;
 }
}

export async function restGet<T>(table:string,query:string):Promise<T>{
 if(!url||!publishableKey)throw new Error('Supabase is not configured');
 const res=await fetch(`${url}/rest/v1/${table}?${query}`,{
   headers:await headers({'Content-Type':'application/json'})
 });
 if(!res.ok)throw new Error(await res.text());
 return await res.json() as T;
}


export async function restRpc<T>(fn:string,body:unknown={}):Promise<T>{
 if(!url||!publishableKey)throw new Error('Supabase is not configured');
 const res=await fetch(`${url}/rest/v1/rpc/${encodeURIComponent(fn)}`,{
   method:'POST',
   headers:await headers({'Content-Type':'application/json'}),
   body:JSON.stringify(body)
 });
 const text=await res.text();
 if(!res.ok)throw new Error(text||`RPC ${fn} failed`);
 return (text?JSON.parse(text):null) as T;
}

export async function restUpsert(table:string,body:unknown,conflict='id'){
 if(!url||!publishableKey)throw new Error('Supabase is not configured');
 const res=await fetch(`${url}/rest/v1/${table}?on_conflict=${encodeURIComponent(conflict)}`,{
   method:'POST',
   headers:await headers({
     'Content-Type':'application/json',
     Prefer:'resolution=merge-duplicates,return=minimal'
   }),
   body:JSON.stringify(body)
 });
 if(!res.ok)throw new Error(await res.text());
}

export async function restDelete(table:string,filter:string){
 if(!url||!publishableKey)throw new Error('Supabase is not configured');
 const res=await fetch(`${url}/rest/v1/${table}?${filter}`,{
   method:'DELETE',
   headers:await headers({Prefer:'return=minimal'})
 });
 if(!res.ok)throw new Error(await res.text());
}

export async function uploadToStorage(path:string,file:File){
 if(!url||!publishableKey)throw new Error('Supabase is not configured');
 const res=await fetch(`${url}/storage/v1/object/${MEDIA_BUCKET}/${path}`,{
   method:'POST',
   headers:await headers({
     'Content-Type':file.type||'application/octet-stream',
     'x-upsert':'false'
   }),
   body:file
 });
 if(!res.ok)throw new Error(await res.text());
 return `${url}/storage/v1/object/public/${MEDIA_BUCKET}/${path}`;
}


// === MIELLEY ACCOUNT AUTH V2.4 ===
// Customer-account helpers for the existing custom REST Supabase auth layer.

async function fetchAuthUser(accessToken:string){
 if(!url||!publishableKey||!accessToken)return null;
 try{
   const res=await fetch(`${url}/auth/v1/user`,{
     headers:{
       apikey:publishableKey,
       Authorization:`Bearer ${accessToken}`
     }
   });
   if(!res.ok)return null;
   return await res.json();
 }catch{
   return null;
 }
}

export async function getCurrentUser(){
 const session=await getSession();
 if(!session?.access_token)return null;

 if(session.user)return session.user as any;

 const user=await fetchAuthUser(session.access_token);
 if(user){
   writeSession({...session,user});
 }
 return user;
}

export async function consumeAuthRedirect(){
 if(!url||!publishableKey){
   return {type:null as string|null,session:null as Session|null};
 }

 const rawHash=window.location.hash?.replace(/^#/,'')||'';
 if(!rawHash){
   return {type:null as string|null,session:await getSession()};
 }

 const params=new URLSearchParams(rawHash);
 const accessToken=params.get('access_token');
 const refreshToken=params.get('refresh_token')||undefined;
 const type=params.get('type');
 const expiresIn=Number(params.get('expires_in')||'3600');

 if(!accessToken){
   return {type,session:await getSession()};
 }

 const user=await fetchAuthUser(accessToken);
 const session:Session={
   access_token:accessToken,
   refresh_token:refreshToken,
   expires_at:Math.floor(Date.now()/1000)+(Number.isFinite(expiresIn)?expiresIn:3600),
   user:user||undefined
 };

 writeSession(session);

 try{
   history.replaceState(null,'',window.location.pathname+window.location.search);
 }catch{}

 return {type,session};
}

export async function signUpAccount(
 email:string,
 password:string,
 metadata:Record<string,unknown>={},
 redirectTo?:string
){
 if(!url||!publishableKey){
   return {data:null,error:new Error('Supabase is not configured')};
 }

 try{
   const endpoint=redirectTo
     ? `${url}/auth/v1/signup?redirect_to=${encodeURIComponent(redirectTo)}`
     : `${url}/auth/v1/signup`;

   const res=await fetch(endpoint,{
     method:'POST',
     headers:{
       apikey:publishableKey,
       'Content-Type':'application/json'
     },
     body:JSON.stringify({
       email,
       password,
       data:metadata
     })
   });

   const data=await res.json().catch(()=>null);

   if(!res.ok){
     const message=data?.msg||data?.message||data?.error_description||'Signup failed';
     return {data:null,error:new Error(String(message))};
   }

   if(data?.access_token){
     writeSession({
       ...data,
       expires_at:Math.floor(Date.now()/1000)+(data.expires_in||3600)
     });
   }

   return {data,error:null};
 }catch(err:any){
   return {data:null,error:err instanceof Error?err:new Error('Signup failed')};
 }
}

export async function sendPasswordReset(email:string,redirectTo?:string){
 if(!url||!publishableKey){
   return {error:new Error('Supabase is not configured')};
 }

 try{
   const endpoint=redirectTo
     ? `${url}/auth/v1/recover?redirect_to=${encodeURIComponent(redirectTo)}`
     : `${url}/auth/v1/recover`;

   const res=await fetch(endpoint,{
     method:'POST',
     headers:{
       apikey:publishableKey,
       'Content-Type':'application/json'
     },
     body:JSON.stringify({email})
   });

   if(!res.ok){
     const data=await res.json().catch(()=>null);
     return {error:new Error(String(data?.msg||data?.message||'Password reset failed'))};
   }

   return {error:null};
 }catch(err:any){
   return {error:err instanceof Error?err:new Error('Password reset failed')};
 }
}

export async function updatePassword(password:string){
 if(!url||!publishableKey){
   return {error:new Error('Supabase is not configured')};
 }

 const session=await getSession();
 if(!session?.access_token){
   return {error:new Error('No active recovery session')};
 }

 try{
   const res=await fetch(`${url}/auth/v1/user`,{
     method:'PUT',
     headers:{
       apikey:publishableKey,
       Authorization:`Bearer ${session.access_token}`,
       'Content-Type':'application/json'
     },
     body:JSON.stringify({password})
   });

   const data=await res.json().catch(()=>null);

   if(!res.ok){
     return {error:new Error(String(data?.msg||data?.message||'Password update failed'))};
   }

   if(data){
     writeSession({...session,user:data});
   }

   return {error:null};
 }catch(err:any){
   return {error:err instanceof Error?err:new Error('Password update failed')};
 }
}


// === MIELLEY ACCOUNT MAGIC V2.6 ===
export async function updateAccountMetadata(metadata:Record<string,unknown>){
 if(!url||!publishableKey){
   return {data:null,error:new Error('Supabase is not configured')};
 }

 const session=await getSession();
 if(!session?.access_token){
   return {data:null,error:new Error('No active account session')};
 }

 try{
   const current=await getCurrentUser() as any;
   const currentMetadata=
     current && typeof current.user_metadata==='object' && current.user_metadata
       ? current.user_metadata
       : {};

   const res=await fetch(`${url}/auth/v1/user`,{
     method:'PUT',
     headers:{
       apikey:publishableKey,
       Authorization:`Bearer ${session.access_token}`,
       'Content-Type':'application/json'
     },
     body:JSON.stringify({
       data:{...currentMetadata,...metadata}
     })
   });

   const data=await res.json().catch(()=>null);

   if(!res.ok){
     return {
       data:null,
       error:new Error(String(data?.msg||data?.message||'Account update failed'))
     };
   }

   if(data){
     writeSession({...session,user:data});
   }

   return {data,error:null};
 }catch(err:any){
   return {
     data:null,
     error:err instanceof Error ? err : new Error('Account update failed')
   };
 }
}
